/**
 * SEO utility functions for product detail pages
 */

/**
 * Generate dynamic meta tags for product pages
 * @param {Object} product - Product data from API
 * @param {string} currentUrl - Current page URL
 * @returns {Object} SEO metadata object
 */
export const generateProductSEO = (product, currentUrl) => {
  if (!product) return {};

  // Generate dynamic title with product name and category
  const title = `${product.name} - ${product.category_details?.name || 'Perfect Gift'} | Giftsy`;
  
  // Create comprehensive description
  const description = generateMetaDescription(product);
  
  // Generate keywords from product data
  const keywords = generateKeywords(product);
  
  // Create canonical URL
  const canonicalUrl = `${window.location.origin}/products/${product.slug || product.id}`;
  
  return {
    title,
    description,
    keywords,
    canonicalUrl,
    openGraph: generateOpenGraphData(product, currentUrl),
    twitterCard: generateTwitterCardData(product),
    structuredData: generateStructuredData(product, canonicalUrl)
  };
};

/**
 * Generate meta description from product data
 * @param {Object} product - Product data
 * @returns {string} Meta description
 */
const generateMetaDescription = (product) => {
  const parts = [];
  
  // Add product description (truncated)
  if (product.description) {
    parts.push(product.description.substring(0, 120));
  }
  
  // Add success rate if available
  if (product.success_rate) {
    parts.push(`${product.success_rate}% gift success rate`);
  }
  
  // Add price information
  if (product.current_price) {
    parts.push(`Starting at $${product.current_price}`);
  }
  
  // Add category information
  if (product.category_details?.name) {
    parts.push(`Perfect ${product.category_details.name.toLowerCase()} gift`);
  }
  
  return parts.join('. ').substring(0, 160);
};

/**
 * Generate keywords from product data
 * @param {Object} product - Product data
 * @returns {string} Comma-separated keywords
 */
const generateKeywords = (product) => {
  const keywords = new Set();
  
  // Add product name words
  if (product.name) {
    product.name.split(' ').forEach(word => {
      if (word.length > 2) keywords.add(word.toLowerCase());
    });
  }
  
  // Add brand
  if (product.brand) {
    keywords.add(product.brand.toLowerCase());
  }
  
  // Add category
  if (product.category_details?.name) {
    keywords.add(product.category_details.name.toLowerCase());
  }
  
  // Add common gift-related keywords
  keywords.add('gifts');
  keywords.add('gift ideas');
  keywords.add('perfect gift');
  keywords.add('giftsy');
  
  // Add occasion-based keywords if available
  if (product.occasions && product.occasions.length > 0) {
    product.occasions.forEach(occasion => {
      keywords.add(occasion.toLowerCase());
    });
  }
  
  return Array.from(keywords).join(', ');
};

/**
 * Generate Open Graph metadata
 * @param {Object} product - Product data
 * @param {string} currentUrl - Current page URL
 * @returns {Object} Open Graph data
 */
const generateOpenGraphData = (product, currentUrl) => {
  return {
    title: `${product.name} - Perfect Gift | Giftsy`,
    description: product.description || generateMetaDescription(product),
    image: product.images?.[0]?.image || '/images/default-product.jpg',
    url: currentUrl,
    type: 'product',
    siteName: 'Giftsy',
    locale: 'en_US',
    // Product-specific Open Graph tags
    'product:price:amount': product.current_price,
    'product:price:currency': product.price_currency || 'USD',
    'product:availability': 'in stock',
    'product:condition': 'new',
    'product:retailer_item_id': product.id,
    'product:brand': product.brand
  };
};

/**
 * Generate Twitter Card metadata
 * @param {Object} product - Product data
 * @returns {Object} Twitter Card data
 */
const generateTwitterCardData = (product) => {
  return {
    card: 'summary_large_image',
    site: '@giftsy',
    title: `${product.name} - Perfect Gift | Giftsy`,
    description: product.description || generateMetaDescription(product),
    image: product.images?.[0]?.image || '/images/default-product.jpg',
    'image:alt': `${product.name} - ${product.category_details?.name || 'Gift'}`
  };
};

/**
 * Generate structured data (JSON-LD) for search engines
 * @param {Object} product - Product data
 * @param {string} canonicalUrl - Canonical URL
 * @returns {Object} Structured data object
 */
const generateStructuredData = (product, canonicalUrl) => {
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images?.map(img => img.image) || [],
    "url": canonicalUrl,
    "sku": product.sku || product.id,
    "mpn": product.mpn || product.id,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Giftsy"
    },
    "category": product.category_details?.name,
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": product.price_currency || "USD",
      "price": product.current_price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Giftsy"
      }
    }
  };

  // Add aggregate rating if available
  if (product.external_rating && product.external_rating_count) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.external_rating,
      "reviewCount": product.external_rating_count,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  // Add additional product properties
  if (product.gift_features_text) {
    structuredData.additionalProperty = {
      "@type": "PropertyValue",
      "name": "Gift Features",
      "value": product.gift_features_text
    };
  }

  // Add breadcrumb structured data
  const breadcrumbData = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Gift Finder",
        "item": `${window.location.origin}/gift-finder`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.category_details?.name || "Products",
        "item": `${window.location.origin}/categories/${product.category_details?.slug || 'all'}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": product.name,
        "item": canonicalUrl
      }
    ]
  };

  return {
    product: structuredData,
    breadcrumb: breadcrumbData
  };
};

/**
 * Generate FAQ structured data for product pages
 * @param {Object} product - Product data
 * @returns {Object} FAQ structured data
 */
export const generateFAQStructuredData = (product) => {
  const faqs = [];

  // Add common product FAQs
  if (product.current_price) {
    faqs.push({
      "@type": "Question",
      "name": `How much does ${product.name} cost?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `${product.name} is priced at $${product.current_price}${product.price_currency ? ` ${product.price_currency}` : ''}.`
      }
    });
  }

  if (product.gift_features_text) {
    faqs.push({
      "@type": "Question",
      "name": `What makes ${product.name} a good gift?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": product.gift_features_text
      }
    });
  }

  if (product.success_rate) {
    faqs.push({
      "@type": "Question",
      "name": `How successful is ${product.name} as a gift?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `${product.name} has a ${product.success_rate}% success rate with gift recipients, making it a highly recommended choice.`
      }
    });
  }

  if (faqs.length === 0) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "FAQPage",
    "mainEntity": faqs
  };
};

/**
 * Update page title dynamically
 * @param {string} title - New page title
 */
export const updatePageTitle = (title) => {
  if (typeof document !== 'undefined') {
    document.title = title;
  }
};

/**
 * Generate social sharing URLs
 * @param {Object} product - Product data
 * @param {string} currentUrl - Current page URL
 * @returns {Object} Social sharing URLs
 */
export const generateSocialSharingUrls = (product, currentUrl) => {
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(`Check out ${product.name} on Giftsy!`);
  const encodedDescription = encodeURIComponent(product.description || `Perfect gift: ${product.name}`);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}&media=${encodeURIComponent(product.images?.[0]?.image || '')}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
};

/**
 * Generate robots meta tag content based on product status
 * @param {Object} product - Product data
 * @returns {string} Robots meta content
 */
export const generateRobotsContent = (product) => {
  if (!product) return 'noindex, nofollow';
  
  // Don't index products that are out of stock or discontinued
  if (product.status === 'discontinued' || product.availability === 'out_of_stock') {
    return 'noindex, follow';
  }
  
  // Index active products with good ratings
  if (product.external_rating && product.external_rating >= 3.5) {
    return 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
  }
  
  return 'index, follow';
};

/**
 * Generate hreflang tags for international SEO
 * @param {string} productSlug - Product slug
 * @returns {Array} Array of hreflang objects
 */
export const generateHreflangTags = (productSlug) => {
  const baseUrl = window.location.origin;
  const languages = [
    { code: 'en', region: 'US', label: 'English (US)' },
    { code: 'es', region: 'ES', label: 'Spanish (Spain)' },
    { code: 'fr', region: 'FR', label: 'French (France)' },
    { code: 'de', region: 'DE', label: 'German (Germany)' }
  ];
  
  return languages.map(lang => ({
    hreflang: `${lang.code}-${lang.region}`,
    href: `${baseUrl}/${lang.code}/products/${productSlug}`
  }));
};

/**
 * Generate product availability schema
 * @param {Object} product - Product data
 * @returns {string} Schema.org availability URL
 */
export const getProductAvailability = (product) => {
  if (!product) return 'https://schema.org/OutOfStock';
  
  switch (product.availability?.toLowerCase()) {
    case 'in_stock':
    case 'available':
      return 'https://schema.org/InStock';
    case 'limited_availability':
      return 'https://schema.org/LimitedAvailability';
    case 'pre_order':
      return 'https://schema.org/PreOrder';
    case 'out_of_stock':
    case 'unavailable':
      return 'https://schema.org/OutOfStock';
    default:
      return 'https://schema.org/InStock';
  }
};

/**
 * Generate product condition schema
 * @param {Object} product - Product data
 * @returns {string} Schema.org condition URL
 */
export const getProductCondition = (product) => {
  if (!product) return 'https://schema.org/NewCondition';
  
  switch (product.condition?.toLowerCase()) {
    case 'new':
      return 'https://schema.org/NewCondition';
    case 'used':
      return 'https://schema.org/UsedCondition';
    case 'refurbished':
      return 'https://schema.org/RefurbishedCondition';
    case 'damaged':
      return 'https://schema.org/DamagedCondition';
    default:
      return 'https://schema.org/NewCondition';
  }
};

/**
 * Track SEO events for analytics
 * @param {string} event - Event name
 * @param {Object} data - Event data
 */
export const trackSEOEvent = (event, data = {}) => {
  // Track with Google Analytics if available
  if (typeof gtag !== 'undefined') {
    gtag('event', event, {
      event_category: 'SEO',
      ...data
    });
  }
  
  // Track with other analytics services
  if (typeof analytics !== 'undefined') {
    analytics.track(event, {
      category: 'SEO',
      ...data
    });
  }
  
  console.log(`SEO Event: ${event}`, data);
};

/**
 * Validate and clean meta description
 * @param {string} description - Raw description
 * @returns {string} Cleaned description
 */
export const cleanMetaDescription = (description) => {
  if (!description) return '';
  
  return description
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s.,!?-]/g, '') // Remove special characters except basic punctuation
    .trim()
    .substring(0, 160); // Limit to 160 characters
};

/**
 * Generate product schema with enhanced data
 * @param {Object} product - Product data
 * @param {string} canonicalUrl - Canonical URL
 * @returns {Object} Enhanced product schema
 */
export const generateEnhancedProductSchema = (product, canonicalUrl) => {
  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "description": cleanMetaDescription(product.description),
    "image": product.images?.map(img => img.image) || [],
    "url": canonicalUrl,
    "sku": product.sku || product.id?.toString(),
    "mpn": product.mpn || product.id?.toString(),
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Giftsy"
    },
    "category": product.category_details?.name,
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": product.price_currency || "USD",
      "price": product.current_price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": getProductAvailability(product),
      "itemCondition": getProductCondition(product),
      "seller": {
        "@type": "Organization",
        "name": "Giftsy",
        "url": window.location.origin
      }
    }
  };

  // Add aggregate rating if available
  if (product.external_rating && product.external_rating_count) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.external_rating,
      "reviewCount": product.external_rating_count,
      "bestRating": 5,
      "worstRating": 1
    };
  }

  // Add additional properties
  if (product.gift_features_text) {
    schema.additionalProperty = [{
      "@type": "PropertyValue",
      "name": "Gift Features",
      "value": product.gift_features_text
    }];
  }

  // Add success rate as additional property
  if (product.success_rate) {
    if (!schema.additionalProperty) schema.additionalProperty = [];
    schema.additionalProperty.push({
      "@type": "PropertyValue",
      "name": "Gift Success Rate",
      "value": `${product.success_rate}%`
    });
  }

  return schema;
};